import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { reduxApiClient } from 'infra'
import { ApiProvider } from 'Core'
import localforage from 'localforage'

const cloudProviderEndpoints = {
  AWS: 'awsresources',
  AZURE: 'azureresources',
  GCP: 'gcpresources'
}

/** =========================
 * localForage (IndexedDB) cache helpers
 * ========================= */

const cloudCacheStore = localforage.createInstance({
  name: 'privilege',
  storeName: 'cloud_resources_cache',
  description: 'Cached cloud resources by cloudType'
})

const cacheKey = (cloudType) => `cloudResources:${cloudType}:all`

/** Entries older than this are considered stale: the next fetch goes to the
 * network instead of the cache (boot rehydrate still paints stale data). */
const CACHE_TTL_MS = 15 * 60 * 1000 // 15 minutes

const readCache = async (cloudType) => {
  try {
    const payload = await cloudCacheStore.getItem(cacheKey(cloudType))
    if (!payload || typeof payload !== 'object') return null

    const ts = Number(payload.ts) || 0
    if (!ts) return null

    const data = payload.data
    if (!Array.isArray(data)) return null

    return { ts, data, stale: Date.now() - ts > CACHE_TTL_MS }
  } catch (e) {
    console.warn(`[cloudResources] localForage read failed for ${cloudType}:`, e)
    return null
  }
}

const writeCache = async (cloudType, data) => {
  try {
    const ts = Date.now()
    await cloudCacheStore.setItem(cacheKey(cloudType), { ts, data })
    return ts
  } catch (e) {
    console.warn(`[cloudResources] localForage write failed for ${cloudType}:`, e)
    return Date.now()
  }
}

/** Drops every cached cloud-resource entry. Called when the logged-in user
 * changes so the next user never rehydrates the previous user's inventory. */
export const clearCloudResourcesCache = async () => {
  try {
    await cloudCacheStore.clear()
  } catch (e) {
    console.warn('[cloudResources] localForage clear failed:', e)
  }
}

const cloudTypeToSliceName = {
  AWS: 'awsResources',
  GCP: 'gcpResources',
  AZURE: 'azureResources'
}

/** =========================
 * Thunks
 * ========================= */

export const rehydrateCloudResourcesFromCache = createAsyncThunk(
  'cloudResources/rehydrateFromCache',
  async ({ cloudTypes = ['AWS', 'GCP', 'AZURE'] } = {}, { dispatch }) => {
    await Promise.all(
      cloudTypes.map(async (cloudType) => {
        const cached = await readCache(cloudType)
        if (cached?.data) {
          dispatch(
            hydrateCloudAll({
              cloudType,
              data: cached.data,
              ts: cached.ts
            })
          )

          // Also populate individual slices (awsResources, gcpResources, azureResources)
          const sliceName = cloudTypeToSliceName[cloudType.toUpperCase()]
          if (sliceName) {
            dispatch({
              type: `UPDATE_SLICE_MAP/ALL/${sliceName}`,
              payload: cached.data
            })
          }
        }
      })
    )
    return true
  }
)

export const fetchCloudResource = createAsyncThunk(
  'cloudResources/fetch',
  async ({ cloudType, resourceType }, { rejectWithValue }) => {
    try {
      const endpoint = cloudProviderEndpoints[cloudType.toUpperCase()]
      if (!endpoint) {
        throw new Error(`Invalid cloudType: ${cloudType}`)
      }
      const apiClient = reduxApiClient(endpoint)
      const data = await apiClient.getByDataParams({
        ['ObjectMeta.Namespace']: resourceType
      })
      return { resourceType: `${cloudType}_${resourceType}`, data: data || [] }
    } catch (err) {
      console.error(`Failed fetching resources for type ${resourceType}:`, err)
      return rejectWithValue(err.toString())
    }
  }
)

export const fetchChildResources = createAsyncThunk(
  'cloudResources/fetchChildResources',
  async ({ parentId }, { rejectWithValue }) => {
    try {
      const apiClient = reduxApiClient('awsresources')
      const data = await apiClient.getByDataParams({
        'Spec.Parent.RefID': parentId
      })
      return { parentId, data: data || [] }
    } catch (err) {
      console.error(`Failed fetching child resources for parent ${parentId}:`, err)
      return rejectWithValue(err.toString())
    }
  }
)

export const fetchAllCloudResources = createAsyncThunk(
  'cloudResources/fetchAll',
  async ({ cloudType, forceRefresh = false }, { rejectWithValue, dispatch }) => {
    if (!forceRefresh) {
      const cached = await readCache(cloudType)
      // Fresh cache short-circuits the network; a stale entry still gets
      // painted by the boot-time rehydrate, but the next fetch goes live.
      if (cached && !cached.stale) {
        // Also populate individual slices (awsResources, gcpResources, azureResources)
        const sliceName = cloudTypeToSliceName[cloudType.toUpperCase()]
        if (sliceName) {
          dispatch({
            type: `UPDATE_SLICE_MAP/ALL/${sliceName}`,
            payload: cached.data
          })
        }

        return { cloudType, allData: cached.data, fromCache: true, ts: cached.ts }
      }
    }

    try {
      const response = await new ApiProvider('resourcetypes')
        .setInstance({
          ObjectMeta: { Name: cloudType.toLowerCase() }
        })
        .get()

      if (!response?.data?.Types?.Elems) {
        throw new Error(`No types found for cloud type: ${cloudType}`)
      }

      const types = response.data.Types.Elems
      const endpoint = cloudProviderEndpoints[cloudType.toUpperCase()]
      const apiClient = reduxApiClient(endpoint)

      const allResources = []
      const batchSize = 5
      for (let i = 0; i < types.length; i += batchSize) {
        const batch = types.slice(i, i + batchSize)
        const fetchPromises = batch.map(async (type) => {
          try {
            const data = await apiClient.getByDataParams({
              ['ObjectMeta.Namespace']: type
            })
            return data || []
          } catch (err) {
            console.error(`Failed fetching resources for type ${type}:`, err)
            return []
          }
        })
        const batchResources = await Promise.all(fetchPromises)
        batchResources.forEach(data => allResources.push(...data))
        if (i + batchSize < types.length) {
          await new Promise((resolve) => setTimeout(resolve, 1000))
        }
      }

      const ts = await writeCache(cloudType, allResources)
      return { cloudType, allData: allResources, ts }
    } catch (err) {
      console.error('Error in fetchAllCloudResources:', err)
      return rejectWithValue(err.toString())
    }
  },
  {
    condition: ({ cloudType, forceRefresh }, { getState }) => {
      if (forceRefresh) return true
      const state = getState().cloudResources
      const entry = state[`${cloudType}_all`]
      if (entry?.loading) return false
      // If loaded in memory and not stale (5 mins in memory), skip
      if (entry?.loaded && Date.now() - entry.lastFetched < 5 * 60 * 1000) {
        return false
      }
      return true
    }
  }
)

const cloudResourcesSlice = createSlice({
  name: 'cloudResources',
  initialState: {
    _rehydrated: false
  },
  reducers: {
    hydrateCloudAll: (state, action) => {
      const { cloudType, data, ts } = action.payload || {}
      if (!cloudType) return

      const key = `${cloudType}_all`
      state[key] = {
        loading: false,
        loaded: true,
        data: Array.isArray(data) ? data : [],
        error: null,
        lastFetched: ts || Date.now()
      }
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(rehydrateCloudResourcesFromCache.fulfilled, (state) => {
        state._rehydrated = true
      })
      .addCase(fetchAllCloudResources.pending, (state, action) => {
        const { cloudType } = action.meta.arg
        const key = `${cloudType}_all`
        state[key] = {
          ...state[key],
          loading: true,
          error: null
        }
      })
      .addCase(fetchAllCloudResources.fulfilled, (state, action) => {
        const { cloudType, allData, ts } = action.payload
        const key = `${cloudType}_all`
        state[key] = {
          loading: false,
          loaded: true,
          data: allData,
          error: null,
          lastFetched: ts || Date.now()
        }
      })
      .addCase(fetchAllCloudResources.rejected, (state, action) => {
        const { cloudType } = action.meta.arg
        const key = `${cloudType}_all`
        state[key] = {
          ...state[key],
          loading: false,
          error: action.payload
        }
      })
      .addCase(fetchChildResources.pending, (state, action) => {
        const { parentId } = action.meta.arg
        state[`children_of_${parentId}`] = {
          loading: true,
          loaded: false,
          data: [],
          error: null
        }
      })
      .addCase(fetchChildResources.fulfilled, (state, action) => {
        const { parentId, data } = action.payload
        state[`children_of_${parentId}`] = {
          loading: false,
          loaded: true,
          data,
          error: null
        }
      })
      .addCase(fetchChildResources.rejected, (state, action) => {
        const { parentId } = action.meta.arg
        state[`children_of_${parentId}`] = {
          loading: false,
          loaded: false,
          data: [],
          error: action.payload
        }
      })
      .addCase(fetchCloudResource.pending, (state, action) => {
        const { resourceType } = action.meta.arg
        state[`${action.meta.arg.cloudType}_${resourceType}`] = {
          loading: true,
          loaded: false,
          data: [],
          error: null
        }
      })
      .addCase(fetchCloudResource.fulfilled, (state, action) => {
        state[action.payload.resourceType] = {
          loading: false,
          loaded: true,
          data: action.payload.data,
          error: null,
          lastFetched: Date.now(),
        };
      })
      .addCase(fetchCloudResource.rejected, (state, action) => {
        const { resourceType, cloudType } = action.meta.arg;
        state[`${cloudType}_${resourceType}`] = {
          loading: false,
          loaded: false,
          data: [],
          error: action.payload,
        };
      })
  }
})

export const { hydrateCloudAll } = cloudResourcesSlice.actions
export default cloudResourcesSlice.reducer

